import React, { useState } from "react";
import styled from "styled-components";
import { SiteData, Link as RouterLink } from "react-static";
import { HashLink } from "react-router-hash-link";
import FaBriefcase from "react-icons/lib/fa/briefcase";
import FaBars from "react-icons/lib/fa/bars";
import FaTimes from "react-icons/lib/fa/close";
import { Logo } from "../containers/Layout";

const NavLogo = styled(Logo)`
  fill: white;
  width: 2em;
  flex-shrink: 0;
`;

const StickedNavigation = styled.nav`
  height: 4.5em;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(30deg, rgb(87, 124, 255), rgb(111, 54, 255));
  box-sizing: border-box;
  position: fixed;
  top: 0;
  z-index: 1000;
`;

const NavInner = styled.div`
  width: 100%;
  max-width: 1380px;
  display: flex;
  align-items: center;
  gap: 2.5em;
  padding: 0 1.5em;
  box-sizing: border-box;
  height: 100%;
`;

const LogoLink = styled(RouterLink)`
  display: flex;
  align-items: center;
  gap: 0.75em;
  text-decoration: none;
  flex-shrink: 0;
`;

const Title = styled.span`
  font-family: "Raleway", sans-serif;
  font-size: 1.5em;
  font-weight: 700;
  color: white;
  white-space: nowrap;

  @media (max-width: 768px) {
    display: none;
  }
`;

const NavLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 2em;

  @media (max-width: 768px) {
    display: none;
  }
`;

const Link = styled(HashLink)`
  font-family: "Raleway", sans-serif;
  font-size: 1em;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  white-space: nowrap;
  transition: color 0.2s;

  &:hover {
    color: white;
  }
`;

const JobsButton = styled(RouterLink)`
  display: inline-flex;
  align-items: center;
  gap: 0.5em;
  margin-left: auto;
  padding: 0.5em 1.25em;
  border-radius: 2em;
  border: 2px solid rgba(255, 255, 255, 0.6);
  background: rgba(255, 255, 255, 0.1);
  color: white;
  font-family: "Raleway", sans-serif;
  font-size: 0.9em;
  font-weight: 600;
  text-decoration: none;
  white-space: nowrap;
  transition: background 0.2s, border-color 0.2s, color 0.2s;
  position: relative;

  &:hover {
    background: white;
    border-color: white;
    color: rgb(111, 54, 255);
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const HamburgerButton = styled.button`
  display: none;
  align-items: center;
  justify-content: center;
  margin-left: auto;
  background: none;
  border: none;
  color: white;
  font-size: 1.5em;
  cursor: pointer;
  padding: 0.25em;
  line-height: 1;

  @media (max-width: 768px) {
    display: flex;
  }
`;

const MobileMenu = styled.div`
  position: fixed;
  top: 4.5em;
  left: 0;
  right: 0;
  z-index: 999;
  background: linear-gradient(160deg, rgb(87, 124, 255), rgb(111, 54, 255));
  display: flex;
  flex-direction: column;
  max-height: ${props => props.isOpen ? "100vh" : "0"};
  overflow: hidden;
  transition: max-height 0.3s ease;

  @media (min-width: 769px) {
    display: none;
  }
`;

const MobileLink = styled(HashLink)`
  font-family: "Raleway", sans-serif;
  font-size: 1.1em;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.85);
  text-decoration: none;
  padding: 1em 2em;
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
  transition: background 0.15s, color 0.15s;

  &:hover {
    color: white;
    background: rgba(255, 255, 255, 0.1);
  }
`;

const MobileJobsLink = styled(RouterLink)`
  display: flex;
  align-items: center;
  gap: 0.6em;
  font-family: "Raleway", sans-serif;
  font-size: 1.1em;
  font-weight: 700;
  color: white;
  text-decoration: none;
  padding: 1em 2em;
  margin: 1em 2em;
  border-radius: 2em;
  border: 2px solid rgba(255, 255, 255, 0.6);
  background: rgba(255, 255, 255, 0.1);
  justify-content: center;
  transition: background 0.2s, color 0.2s;

  &:hover {
    background: white;
    color: rgb(111, 54, 255);
  }
`;

export const links = [
  { to: "/#praxen", children: "Praxen" },
  { to: "/#leistungen", children: "Leistungen" },
  { to: "/#team", children: "Team" },
  { to: "/#kontakt", children: "Kontakt" },
];

export const StickedNavbar = ({ links }) => {
  const [isOpen, setIsOpen] = useState(false);
  const close = () => setIsOpen(false);
  const toggle = () => setIsOpen(o => !o);

  return (
    <SiteData
      render={({ title }) => (
        <React.Fragment>
          <StickedNavigation>
            <NavInner>
              <LogoLink to="/" onClick={close}>
                <NavLogo />
                <Title>{title}</Title>
              </LogoLink>
              <NavLinks>
                {links.map((link, i) => (
                  <Link key={i} {...link} />
                ))}
              </NavLinks>
              <JobsButton to="/jobs">
                <FaBriefcase />
                Jobs
              </JobsButton>
              <HamburgerButton onClick={toggle} aria-label="Menü öffnen">
                {isOpen ? <FaTimes /> : <FaBars />}
              </HamburgerButton>
            </NavInner>
          </StickedNavigation>
          <MobileMenu isOpen={isOpen}>
            {links.map((link, i) => (
              <MobileLink key={i} {...link} onClick={close} />
            ))}
            <MobileJobsLink to="/jobs" onClick={close}>
              <FaBriefcase />
              Jobs
            </MobileJobsLink>
          </MobileMenu>
        </React.Fragment>
      )}
    />
  );
};



// WEBPACK FOOTER //
// /opt/build/repo/src/segments/NavbarSegment.js